import { Box, Button, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { formatRelative, formatNextScheduled, useMinuteTicker } from '../../utils/schedule';

interface ScheduleBarProps {
  isActive: boolean;
  lastRunAt?: string | null;
  isWorking: boolean;
  runningNow: boolean;
  onRunNow: () => void;
}

/**
 * Universal status row shown on each auto-* config card:
 *   🕐 Last run · 📅 Next scheduled · [▶ Run now]
 *
 * Auto-refreshes once per minute so the relative-time labels stay live.
 * The "Run now" button is disabled when the config is inactive or already
 * busy, mirroring the same UX across liker/commenter/subscriber.
 */
export default function ScheduleBar({
  isActive,
  lastRunAt,
  isWorking,
  runningNow,
  onRunNow,
}: ScheduleBarProps) {
  useMinuteTicker();

  return (
    <Box
      sx={{
        mt: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
        py: 1,
        px: 1.5,
        borderRadius: 1,
        bgcolor: 'background.default',
        border: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <AccessTimeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography variant="caption" color="text.secondary">
          Последний запуск:{' '}
          <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>
            {formatRelative(lastRunAt)}
          </Box>
        </Typography>
      </Box>
      {isActive && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ScheduleIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary">
            По расписанию:{' '}
            <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>
              {formatNextScheduled()}
            </Box>
          </Typography>
        </Box>
      )}
      <Box sx={{ flexGrow: 1 }} />
      <Button
        size="small"
        variant="contained"
        startIcon={
          runningNow || isWorking ? (
            <HourglassEmptyIcon fontSize="small" />
          ) : (
            <PlayArrowIcon />
          )
        }
        disabled={runningNow || isWorking || !isActive}
        onClick={onRunNow}
      >
        {isWorking
          ? 'Выполняется…'
          : runningNow
            ? 'Ставлю в очередь…'
            : 'Запустить сейчас'}
      </Button>
    </Box>
  );
}
